import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Music, Play, Loader2, ArrowLeft, Heart, Disc3 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { usePreviewPlayer } from "@/components/MusicLocation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MusicReels() {
  const { key } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const { playingUrl, toggle } = usePreviewPlayer();

  useEffect(() => {
    axios.get(`${API}/music/${key}/reels`).then((r) => setData(r.data)).catch((e) => setError(e.response?.data?.detail || "Música não encontrada"));
  }, [key]);

  const music = data?.music || state?.music;
  const playing = music && playingUrl === music.preview_url;

  return (
    <div className="max-w-4xl mx-auto space-y-4" data-testid="music-reels-page">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="music-back"><ArrowLeft className="w-4 h-4 mr-1" /> Voltar</Button>
      <Card className="p-6 flex items-center gap-5 flex-wrap bg-gradient-to-br from-slate-900 to-slate-700 text-white border-0">
        <button onClick={() => music && toggle(music.preview_url)} className="relative w-28 h-28 rounded-full overflow-hidden bg-black/40 shrink-0 flex items-center justify-center shadow-2xl" data-testid="music-play-btn" aria-label="Ouvir prévia">
          {music?.cover ? <img src={music.cover} alt="" className={`absolute inset-0 w-full h-full object-cover ${playing ? "animate-spin [animation-duration:5s]" : ""}`} /> : <Disc3 className={`w-14 h-14 ${playing ? "animate-spin [animation-duration:5s]" : ""}`} />}
          <span className="relative w-10 h-10 rounded-full bg-white/90 text-black flex items-center justify-center"><Play className="w-5 h-5 ml-0.5" /></span>
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-wider text-white/60 flex items-center gap-1"><Music className="w-3 h-3" /> Música</div>
          <h1 className="text-2xl font-bold truncate" data-testid="music-title">{music?.title || "..."}</h1>
          <p className="text-white/80 text-sm truncate">{music?.artist || (music?.source === "upload" ? "Áudio próprio" : "")}</p>
          <p className="text-white/60 text-xs mt-2" data-testid="music-reels-count">{data ? `${data.count} vídeo${data.count === 1 ? "" : "s"} usam esta música` : ""}</p>
        </div>
        {data?.reels?.length > 0 && (
          <Button asChild className="rounded-full bg-white text-black hover:bg-white/90" data-testid="music-watch-all">
            <Link to={`/reels?start=${data.reels[0].post_id}`}><Play className="w-4 h-4 mr-1" /> Assistir</Link>
          </Button>
        )}
      </Card>

      {error && <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="music-error">{error}</Card>}
      {!data && !error && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>}
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="music-reels-grid">
          {data.reels.map((v) => (
            <Link key={v.post_id} to={`/reels?start=${v.post_id}`} className="relative aspect-[9/16] rounded-xl overflow-hidden bg-black group" data-testid={`music-reel-${v.post_id}`}>
              {v.cover_url ? <img src={v.cover_url} alt="" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" /> : <video src={v.video_url} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" muted playsInline preload="metadata" />}
              <div className="absolute inset-0 flex items-center justify-center"><Play className="w-8 h-8 text-white drop-shadow" /></div>
              <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black/80 to-transparent text-white text-[11px]">
                <div className="truncate font-semibold">{v.author_name}</div>
                <div className="flex items-center gap-1 text-white/80"><Heart className="w-3 h-3" /> {(v.likes || []).length}</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
